import React, { useState } from "react";
import { API_URLS } from "../../config/apiUrls";
import {
  TextField,
  Button,
  IconButton,
  Snackbar,
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Collapse, // Importação adicionada
} from "@mui/material";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import AddIcon from "@mui/icons-material/Add"; // Ícone para o botão

interface GerenciarAdminAdicionarProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
  onAdminAdded: () => void;
}

const GerenciarAdminAdicionar: React.FC<GerenciarAdminAdicionarProps> = ({
  token,
  role,
  onAdminAdded,
}) => {
  // NOVO ESTADO para controlar a visibilidade do formulário
  const [showForm, setShowForm] = useState(false);

  const [novoUsuario, setNovoUsuario] = useState({
    nome: "",
    email: "",
    senha: "",
    role: "PARTNER", // Role do novo usuário
  });
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Criar novo usuário
  const handleCreateUser = async () => {
    setLoading(true);
    // Validação básica
    if (!novoUsuario.nome || !novoUsuario.email || !novoUsuario.senha) {
      setSnackbarMessage("Por favor, preencha todos os campos obrigatórios.");
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(API_URLS.gerenciar, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: novoUsuario.nome,
          email: novoUsuario.email,
          password: novoUsuario.senha,
          role: novoUsuario.role,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage =
          errorData.message || `Erro ${response.status}: Falha na requisição.`;
        throw new Error(errorMessage);
      }

      setSnackbarMessage("Usuário criado com sucesso!");
      setSnackbarOpen(true);
      // Limpa os campos após o sucesso
      setNovoUsuario({ nome: "", email: "", senha: "", role: "PARTNER" });
      // Esconde o formulário após a criação
      setShowForm(false);
      onAdminAdded();
    } catch (error: any) {
      console.error("Erro ao criar usuário:", error);
      setSnackbarMessage(
        error.message || "Erro desconhecido ao criar usuário."
      );
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Alterar visibilidade da senha
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* BOTÃO PARA MOSTRAR/OCULTAR O FORMULÁRIO */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Ocultar Formulário" : "Adicionar Novo Admin"}
        </Button>
      </Box>

      {/* COLLAPSE para esconder/mostrar o formulário */}
      <Collapse in={showForm}>
        <Box
          sx={{
            mt: 3,
            display: "flex", // Ativa o Flexbox
            flexWrap: "wrap", // Permite que os itens quebrem para a próxima linha
            margin: "-12px", // Compensa o espaçamento (spacing={3} = 24px, então margin: -12px)
            border: "1px solid #ccc", // Adiciona borda para destacar o formulário
            padding: "12px 0", // Ajusta o padding interno
            borderRadius: "4px",
          }}
        >
          {/* Campo NOME: xs=12 (100%), sm=6 (50%) */}
          <Box
            sx={{
              padding: "12px", // Padding para simular spacing={3}
              width: { xs: "100%", sm: "50%" }, // Responsividade: 100% em xs, 50% em sm
            }}
          >
            <TextField
              label="Nome"
              variant="outlined"
              fullWidth
              value={novoUsuario.nome}
              onChange={(e) =>
                setNovoUsuario({ ...novoUsuario, nome: e.target.value })
              }
              required
            />
          </Box>

          {/* Campo E-MAIL: xs=12 (100%), sm=6 (50%) */}
          <Box
            sx={{
              padding: "12px",
              width: { xs: "100%", sm: "50%" },
            }}
          >
            <TextField
              label="E-mail"
              type="email"
              variant="outlined"
              fullWidth
              value={novoUsuario.email}
              onChange={(e) =>
                setNovoUsuario({ ...novoUsuario, email: e.target.value })
              }
              required
            />
          </Box>

          {/* Campo SENHA: xs=12 (100%), sm=6 (50%) */}
          <Box
            sx={{
              padding: "12px",
              width: { xs: "100%", sm: "50%" },
            }}
          >
            <TextField
              label="Senha"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              value={novoUsuario.senha}
              onChange={(e) =>
                setNovoUsuario({ ...novoUsuario, senha: e.target.value })
              }
              InputProps={{
                endAdornment: (
                  <IconButton onClick={togglePasswordVisibility}>
                    {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
                  </IconButton>
                ),
              }}
              required
            />
          </Box>

          {/* Campo ROLE (apenas MASTER): xs=12 (100%), sm=6 (50%) */}
          {role === "MASTER" && (
            <Box
              sx={{
                padding: "12px",
                width: { xs: "100%", sm: "50%" },
              }}
            >
              <FormControl fullWidth variant="outlined" required>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  id="role-select"
                  value={novoUsuario.role}
                  onChange={(e) =>
                    setNovoUsuario({
                      ...novoUsuario,
                      role: e.target.value as "PARTNER" | "ANALYST",
                    })
                  }
                  label="Role"
                >
                  <MenuItem value="PARTNER">PARCEIRO</MenuItem>
                  <MenuItem value="ANALYST">ANALISTA</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Botão de SUBMIT (sempre 100% da largura) */}
          <Box sx={{ padding: "12px", width: "100%", textAlign: "right" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateUser}
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? "Criando..." : "Criar Administrador"}
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* Snackbar de sucesso ou erro */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default GerenciarAdminAdicionar;
